import React, { useCallback, useLayoutEffect, useRef } from 'react';
import { BaseInputTemplateProps } from '@rjsf/utils';
import BaseInputTemplate from '@/components/_common/SchemaForm/Templates/BaseInputTemplate';

const allowedSpecialKeys = [
  'Enter',
  'Backspace',
  'Delete',
  'Alt',
  'Shift',
  'Control',
  'Meta',
  'Tab',
  'Home',
  'End',
  'PageUp',
  'PageDown',
  'ArrowUp',
  'ArrowRight',
  'ArrowDown',
  'ArrowLeft'
];

const FORMATTED_VALUE_LENGTH = '01:23:45;29'.length;
const DIGITS_ONLY_VALUE_LENGTH = '01234529'.length;

const removeNonDigits = (str = '') => str.replace(/[^\d+]/g, '');
// Converts a caret position in the formatted timecode string to the matching index
// in the 8-digit value by ignoring the separators (: and ;).
const getDigitCaretPosition = (caret: number) => caret - Math.floor(caret / 3);

// formatTimecode("00000123", true)  –> "00:00:01;23"
// formatTimecode("00000123", false) –> "00:00:01:23"
const formatTimecode = (str: string, isDropFrame: boolean) => {
  const framesSeparator = isDropFrame ? ';' : ':';

  if (str.length !== DIGITS_ONLY_VALUE_LENGTH) {
    str = str.padStart(DIGITS_ONLY_VALUE_LENGTH, '0');
  }

  const hh = str.substring(0, 2);
  const mm = str.substring(2, 4);
  const ss = str.substring(4, 6);
  const ff = str.substring(6, 8);

  return `${hh}:${mm}:${ss}${framesSeparator}${ff}`;
};

interface Props extends BaseInputTemplateProps {
  value: string | undefined;
}

const TimecodeWidget: React.FC<Props> = (props) => {
  const { value, schema, onChange } = props;
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const prevValue = useRef<string | undefined>(undefined);
  const caretPosition = useRef(0);
  const isDropFrame: boolean = schema.is_dropframe;
  const digitsValue = removeNonDigits(value);

  // Keep the caret in the same position after value changed.
  useLayoutEffect(() => {
    if (value) {
      // If the input already had a value, restore the previously tracked caret position
      // after reformatting. If this is the first populated value, place the caret at the
      // end of the timecode string.
      if (prevValue.current) {
        inputRef.current?.setSelectionRange(caretPosition.current, caretPosition.current);
      } else {
        inputRef.current?.setSelectionRange(FORMATTED_VALUE_LENGTH, FORMATTED_VALUE_LENGTH);
      }
    }

    return () => {
      prevValue.current = value;
    };
  }, [value]);

  const handleKeyDown: React.KeyboardEventHandler = useCallback(
    (e) => {
      const { key } = e;
      const isDigit = key.match(/^\d$/);
      const isSpecialKey = allowedSpecialKeys.includes(e.key);

      if (isSpecialKey) {
        if (key === 'Backspace') {
          if (digitsValue) {
            // Clear the field when only zeros remain, or when Ctrl/Cmd+Backspace pressed.
            if (digitsValue === '00000000' || e.ctrlKey || e.metaKey) {
              onChange(undefined);
            } else {
              const digitPositionAdjusted = getDigitCaretPosition(caretPosition.current);
              const newValue =
                '0' + digitsValue.slice(0, digitPositionAdjusted - 1).concat(digitsValue.slice(digitPositionAdjusted, DIGITS_ONLY_VALUE_LENGTH));
              onChange(formatTimecode(newValue, isDropFrame));
            }
          }
          e.preventDefault();
        } else if (key === 'Delete') {
          // this `preventDefault()` prevent the `onChange` event from being triggered. So we don't have to with this
          // scenario in the `handleChange` function. Also, it prevents the caret from moving to the end position.
          e.preventDefault();
        }
        return;
      }

      // exit when Ctrl or Cmd button press to avoid preventing default when keys combinations such as "Ctrl R" or "Cmd +" are pressed.
      if (e.ctrlKey || e.metaKey) {
        return;
      }

      e.preventDefault();

      if (!isDigit) return;

      if (!digitsValue) {
        // in this branch user basically enters the first digit in the field
        onChange(formatTimecode(`0000000${key}`, isDropFrame));
        return;
      }

      if (caretPosition.current === 0) return; // Nothing to replace before the first digit
      const digitPositionAdjusted = getDigitCaretPosition(caretPosition.current);
      const newValue = digitsValue.slice(1, digitPositionAdjusted) + key + digitsValue.slice(digitPositionAdjusted, DIGITS_ONLY_VALUE_LENGTH);
      onChange(formatTimecode(newValue, isDropFrame));
    },
    [digitsValue, isDropFrame, onChange]
  );

  const handleKeyUp: React.KeyboardEventHandler<HTMLTextAreaElement> = useCallback((e) => {
    caretPosition.current = (e.target as HTMLTextAreaElement).selectionStart;
  }, []);

  const handleChange: React.ChangeEventHandler = useCallback(
    (e) => {
      const target = e.target as HTMLTextAreaElement;

      // handle Ctrl+V and Cmd+V
      const insertedString = target.value.slice(caretPosition.current, -(FORMATTED_VALUE_LENGTH - caretPosition.current) || undefined);
      const insertedDigits = removeNonDigits(insertedString);
      if (insertedDigits) {
        onChange(formatTimecode(insertedDigits, isDropFrame));
      }
    },
    [value, isDropFrame, onChange]
  );

  const handleClick: React.MouseEventHandler = useCallback((e) => {
    caretPosition.current = (e.target as HTMLTextAreaElement).selectionStart;
  }, []);

  return (
    <BaseInputTemplate
      {...props}
      value={value}
      inputRef={inputRef}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      onChangeOverride={handleChange}
      onClick={handleClick}
    />
  );
};

export default TimecodeWidget;
